import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlay, FiStar, FiUsers, FiClock, FiZap, FiAward } from 'react-icons/fi';
import './Games.css';

const Games = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [games, setGames] = useState([]);
  const [categories, setCategories] = useState([{ id: 'all', name: 'Todos', icon: '🎮' }]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadGames = async () => {
      try {
        const res = await fetch('http://localhost:5000/api/games');
        const data = await res.json();

        const normalizedGames = (data.games || []).map(g => ({
          ...g,
          featured: Boolean(g.is_featured),
          comingSoon: Boolean(g.coming_soon)
        }));

        const categoryIcon = {
          all: '🎮',
          trivia: '❓',
          puzzle: '🧩',
          memoria: '🎴',
          aventura: '🗺️',
          educativo: '📚',
          palabras: '📝'
        };

        const normalizedCategories = (data.categories || ['all']).map((c) => {
          if (c === 'all') return { id: 'all', name: 'Todos', icon: '🎮' };
          const label = c.charAt(0).toUpperCase() + c.slice(1);
          return { id: c, name: label, icon: categoryIcon[c] || '🎮' };
        });

        setGames(normalizedGames);
        setCategories(normalizedCategories);
      } catch (error) {
        console.error('Error loading games:', error);
        setGames([]);
        setCategories([{ id: 'all', name: 'Todos', icon: '🎮' }]);
      } finally {
        setLoading(false);
      }
    };

    loadGames();
  }, []);

  const filteredGames = useMemo(() => (
    selectedCategory === 'all'
      ? games
      : games.filter(game => game.category === selectedCategory)
  ), [games, selectedCategory]);

  const featuredGames = useMemo(() => games.filter(g => g.featured && !g.comingSoon), [games]);
  const availableGames = useMemo(() => filteredGames.filter(g => !g.comingSoon), [filteredGames]);
  const comingSoonGames = useMemo(() => filteredGames.filter(g => g.comingSoon), [filteredGames]);

  const categoriesCount = Math.max(categories.length - 1, 0);

  if (loading) {
    return (
      <div className="games-page">
        <section className="games-hero">
          <div className="hero-content">
            <div className="games-logo">
              <span className="logo-icon">🎮</span>
              <span className="logo-text">GRAPHYRA GAMES</span>
            </div>
            <h1>Cargando juegos...</h1>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="games-page">
      {/* Hero Section */}
      <section className="games-hero">
        <div className="hero-particles">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="particle" style={{
              '--delay': `${Math.random() * 5}s`,
              '--x': `${Math.random() * 100}%`,
              '--duration': `${3 + Math.random() * 4}s`
            }}></div>
          ))}
        </div>
        
        <div className="hero-content">
          <div className="games-logo">
            <span className="logo-icon">🎮</span>
            <span className="logo-text">GRAPHYRA GAMES</span>
          </div>
          
          <h1>Aprende <span className="gradient-text">Jugando</span></h1>
          <p>Juegos educativos diseñados para expandir tu mente mientras te diviertes</p>
          
          <div className="hero-stats">
            <div className="stat">
              <FiZap className="stat-icon" />
              <span className="stat-value">{games.filter(g => !g.comingSoon).length}</span>
              <span className="stat-label">Juegos</span>
            </div>
            <div className="stat">
              <FiUsers className="stat-icon" />
              <span className="stat-value">{categoriesCount}</span>
              <span className="stat-label">Categorías</span>
            </div>
            <div className="stat">
              <FiAward className="stat-icon" />
              <span className="stat-value">{games.filter(g => g.comingSoon).length}</span>
              <span className="stat-label">Próximamente</span>
            </div>
          </div>
        </div>
        
        {/* Floating game icons */}
        <div className="floating-icons">
          <span className="float-icon" style={{ '--delay': '0s' }}>🧠</span>
          <span className="float-icon" style={{ '--delay': '0.5s' }}>🎯</span>
          <span className="float-icon" style={{ '--delay': '1s' }}>🏆</span>
          <span className="float-icon" style={{ '--delay': '1.5s' }}>⭐</span>
        </div>
      </section>

      {/* Featured Games */}
      {featuredGames.length > 0 && (
        <section className="featured-games">
          <h2><FiStar className="section-icon" /> Juegos Destacados</h2>
          
          <div className="featured-grid">
            {featuredGames.map(game => (
              <Link 
                key={game.id} 
                to={`/games/${game.id}`} 
                className="featured-game-card"
                style={{ '--game-color': game.color }}
              >
                <div className="game-glow"></div>
                <div className="game-icon">{game.icon}</div>
                <div className="game-info">
                  <h3>{game.title}</h3>
                  <p>{game.description}</p>
                  <div className="game-meta">
                    <span><FiUsers /> {game.players}</span>
                    <span><FiClock /> {game.time}</span>
                  </div>
                </div>
                <button className="play-btn">
                  <FiPlay /> Jugar
                </button>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="games-categories">
        <div className="categories-scroll">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`category-chip ${selectedCategory === cat.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.id)}
            >
              <span className="chip-icon">{cat.icon}</span>
              <span>{cat.name}</span>
            </button>
          ))}
        </div>
      </section>

      {/* All Games Grid */}
      <section className="all-games">
        <h2>🎮 {selectedCategory === 'all' ? 'Todos los Juegos' : categories.find(c => c.id === selectedCategory)?.name}</h2>
        
        {availableGames.length > 0 ? (
          <div className="games-grid">
            {availableGames.map(game => (
              <Link 
                key={game.id} 
                to={`/games/${game.id}`} 
                className="game-card"
                style={{ '--game-color': game.color }}
              >
                <div className="card-header">
                  <span className="game-icon-small">{game.icon}</span>
                  <span className="difficulty-badge">{game.difficulty}</span>
                </div>
                <h3>{game.title}</h3>
                <p>{game.description}</p>
                <div className="card-footer">
                  <div className="game-details">
                    <span><FiUsers /> {game.players}</span>
                    <span><FiClock /> {game.time}</span>
                  </div>
                  <div className="play-indicator">
                    <FiPlay />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="no-games">
            <span className="no-icon">🎮</span>
            <p>No hay juegos disponibles en esta categoría aún</p>
          </div>
        )}
      </section>

      {/* Coming Soon */}
      {comingSoonGames.length > 0 && (
        <section className="coming-soon">
          <h2>🚀 Próximamente</h2>
          <p className="section-desc">Nuevos juegos en desarrollo</p>
          
          <div className="coming-grid">
            {comingSoonGames.map(game => (
              <div key={game.id} className="coming-card" style={{ '--game-color': game.color }}>
                <div className="coming-badge">PRÓXIMAMENTE</div>
                <span className="game-icon-large">{game.icon}</span>
                <h3>{game.title}</h3>
                <p>{game.description}</p>
                <button className="notify-btn">🔔 Notificarme</button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Games;
